import React from 'react';

export interface AppBrandLogoProps {
  /** Outer box (square); image uses object-contain so transparent PNGs show correctly. */
  className?: string;
  alt?: string;
  /** Circular badge. Same gold-on-glass mark in light and dark mode. */
  onCircle?: boolean;
}

/**
 * App mark from `/app-icon.png` (supports transparent background).
 */
export const AppBrandLogo: React.FC<AppBrandLogoProps> = ({
  className = 'w-16 h-16',
  alt = 'Persianist',
  onCircle = false,
}) => (
  <div className={`flex items-center justify-center shrink-0 ${className}`}>
    <img
      src="/app-icon.png"
      alt={alt}
      className={
        onCircle
          ? 'h-full w-full object-contain object-center mix-blend-lighten'
          : 'h-full w-full object-contain object-center opacity-100 [filter:drop-shadow(0_2px_6px_rgb(0_0_0_/_0.28))_contrast(1.08)] dark:[filter:drop-shadow(0_1px_4px_rgb(0_0_0_/_0.5))]'
      }
      decoding="async"
      loading="eager"
      width={256}
      height={256}
    />
  </div>
);

export interface AppBrandLogoFrameProps {
  alt?: string;
  className?: string;
  size?: 'sm' | 'hero';
}

const APP_BRAND_LOGO_FRAME_CLASS =
  'relative flex shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-zinc-800 to-zinc-950 ring-1 ring-amber-500/30 dark:from-zinc-900 dark:to-black dark:ring-amber-500/25';

const APP_BRAND_LOGO_FRAME_SIZES = {
  sm: {
    frame: 'h-12 w-12 p-1.5 shadow-md sm:h-14 sm:w-14 sm:p-2',
    logo: 'h-10 w-10 sm:h-12 sm:w-12',
  },
  hero: {
    frame: 'p-2 shadow-xl dark:p-2.5',
    logo: 'h-[7.25rem] w-[7.25rem] sm:h-28 sm:w-28 md:h-32 md:w-32',
  },
} as const;

/** Home-page style rounded gradient frame around the app mark. */
export const AppBrandLogoFrame: React.FC<AppBrandLogoFrameProps> = ({
  alt = 'Persianist',
  className = '',
  size = 'hero',
}) => {
  const sizing = APP_BRAND_LOGO_FRAME_SIZES[size];
  return (
    <div className={`${APP_BRAND_LOGO_FRAME_CLASS} ${sizing.frame} ${className}`}>
      <AppBrandLogo className={sizing.logo} alt={alt} />
    </div>
  );
};

export default AppBrandLogo;
